import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PlusIcon, UsersIcon, FunnelIcon,
  Squares2X2Icon, ListBulletIcon, XMarkIcon, BuildingOfficeIcon,
  EnvelopeIcon, PhoneIcon, CurrencyDollarIcon, ChartBarIcon,
  FolderIcon, StarIcon, MapPinIcon, TagIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, Button, SearchInput, Select, Avatar,
  StatusBadge, Pagination, EmptyState, PageLoader, StarRating,
  Badge
} from '../../components/ui';
import { clientService } from '../../services';

export default function ClientList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    source: '',
    industry: '',
    sort: '-createdAt'
  });
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {},
    totalRevenue: 0,
    avgScore: 0
  });

  useEffect(() => {
    fetchStatuses();
    fetchSources();
  }, []);

  useEffect(() => {
    fetchClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  useEffect(() => {
    calculateStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const fetchStatuses = async () => {
    try {
      const data = await clientService.getStatuses();
      setStatuses(data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const data = await clientService.getSources();
      setSources(data);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort: filters.sort,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await clientService.getClients(params);
      setClients(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    if (clients.length === 0) {
      setStats({ total: 0, byStatus: {}, totalRevenue: 0, avgScore: 0 });
      return;
    }

    const byStatus = {};
    let totalRevenue = 0;
    let scoreSum = 0;
    let scoreCount = 0;

    clients.forEach(client => {
      byStatus[client.status] = (byStatus[client.status] || 0) + 1;
      totalRevenue += client.totalRevenue || 0;
      if (client.score) {
        scoreSum += client.score;
        scoreCount++;
      }
    });

    setStats({
      total: pagination.total,
      byStatus,
      totalRevenue,
      avgScore: scoreCount > 0 ? Math.round(scoreSum / scoreCount) : 0
    });
  };

  const handleSearch = useCallback((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      source: '',
      industry: '',
      sort: '-createdAt'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const formatCurrency = (amount, currency = 'EGP') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const statusOptions = [
    { value: '', label: t('common.all') },
    ...statuses.map(s => ({ value: s.name, label: s.name }))
  ];

  const sourceOptions = [
    { value: '', label: t('common.all') },
    ...sources.map(s => ({ value: s.name, label: s.name }))
  ];

  const sortOptions = [
    { value: '-createdAt', label: t('common.newest') || 'Newest First' },
    { value: 'createdAt', label: t('common.oldest') || 'Oldest First' },
    { value: 'name', label: t('common.name') + ' (A-Z)' },
    { value: '-name', label: t('common.name') + ' (Z-A)' },
    { value: '-score', label: t('clients.score') + ' (High)' },
    { value: 'score', label: t('clients.score') + ' (Low)' },
    { value: '-totalRevenue', label: t('clients.totalRevenue') + ' (High)' },
    { value: 'totalRevenue', label: t('clients.totalRevenue') + ' (Low)' }
  ];

  // Get unique industries from clients
  const industryOptions = [
    { value: '', label: t('common.all') },
    ...Array.from(new Set(clients.map(c => c.industry).filter(Boolean))).map(industry => ({
      value: industry,
      label: industry
    }))
  ];

  const hasActiveFilters = filters.status || filters.source || filters.industry;

  const statsCards = [
    {
      label: t('common.all'),
      value: stats.total,
      icon: UsersIcon,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: t('clients.totalRevenue'),
      value: formatCurrency(stats.totalRevenue),
      icon: CurrencyDollarIcon,
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      label: t('clients.avgScore') || 'Average Score',
      value: `${stats.avgScore}/5`,
      icon: StarIcon,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      label: Object.keys(stats.byStatus).length > 0 ? t(`clients.statuses.${Object.keys(stats.byStatus)[0].toLowerCase()}`) : t('common.status'),
      value: Object.keys(stats.byStatus).length > 0 ? Object.values(stats.byStatus)[0] : 0,
      icon: ChartBarIcon,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('clients.title')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={t('clients.title')}
        actions={
          <Button icon={PlusIcon} onClick={() => navigate('/clients/new')}>
            {t('clients.newClient')}
          </Button>
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statsCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                  <p className="text-xl font-bold text-neutral-dark">{stat.value}</p>
                </div>
                <div className={`${stat.bgColor} p-2.5 rounded-lg`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col gap-4">
            {/* Main Search and Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex-1 w-full">
                <SearchInput
                  value={filters.search}
                  onChange={handleSearch}
                  placeholder={t('common.search') + ' ' + t('clients.title').toLowerCase() + '...'}
                  className="w-full"
                />
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                    title="Grid View"
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                    title="List View"
                  >
                    <ListBulletIcon className="w-4 h-4" />
                  </button>
                </div>
                {/* Filter Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  icon={FunnelIcon}
                  onClick={() => setShowFilters(!showFilters)}
                  className={hasActiveFilters ? 'border-primary text-primary' : ''}
                >
                  {t('common.filter')}
                  {hasActiveFilters && (
                    <span className="ml-1 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                      {[filters.status, filters.source, filters.industry].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Select
                    label={t('common.status')}
                    options={statusOptions}
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  />
                  <Select
                    label={t('clients.source')}
                    options={sourceOptions}
                    value={filters.source}
                    onChange={(e) => handleFilterChange('source', e.target.value)}
                  />
                  <Select
                    label={t('clients.industry')}
                    options={industryOptions}
                    value={filters.industry}
                    onChange={(e) => handleFilterChange('industry', e.target.value)}
                  />
                  <Select
                    label={t('common.sort') || 'Sort By'}
                    options={sortOptions}
                    value={filters.sort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                  />
                </div>
                {hasActiveFilters && (
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      icon={XMarkIcon}
                    >
                      {t('common.clearFilters') || 'Clear Filters'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Clients List */}
      {loading ? (
        <PageLoader />
      ) : clients.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={UsersIcon}
              title={t('clients.noClients')}
              description={t('clients.noClientsDesc')}
              action={() => navigate('/clients/new')}
              actionLabel={t('clients.newClient')}
            />
          </CardBody>
        </Card>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <Card
                  key={client._id}
                  className="hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => navigate(`/clients/${client._id}`)}
                >
                  <CardBody>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Avatar
                          src={client.photo ? (client.photo.startsWith('http') ? client.photo : process.env.REACT_APP_SOCKET_URL + client.photo) : null}
                          name={client.name}
                          size="md"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">
                            {client.name}
                          </h3>
                          {client.company && (
                            <p className="text-sm text-gray-500 truncate">{client.company}</p>
                          )}
                        </div>
                      </div>
                      <StatusBadge status={client.status} />
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-2 mb-4 text-sm">
                      {client.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <EnvelopeIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{client.email}</span>
                        </div>
                      )}
                      {client.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <PhoneIcon className="w-4 h-4 flex-shrink-0" />
                          <span>{client.phone}</span>
                        </div>
                      )}
                      {client.address?.city && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">
                            {client.address.city}
                            {client.address.country && `, ${client.address.country}`}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{t('projects.title')}</p>
                        <p className="font-semibold text-lg text-primary">
                          {client.projectCount || client.projects?.length || 0}
                        </p>
                      </div>
                      {client.totalRevenue > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('clients.totalRevenue')}</p>
                          <p className="font-semibold text-lg text-success">
                            {formatCurrency(client.totalRevenue, client.currency)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Score */}
                    {client.score > 0 && (
                      <div className="flex items-center justify-between mb-4 p-3 bg-accent/5 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">{t('clients.score')}</span>
                        <div className="flex items-center gap-2">
                          <StarRating value={client.score} max={5} readonly size="sm" />
                          <span className="text-sm font-semibold text-accent">{client.score}/5</span>
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {client.tags && client.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {client.tags.slice(0, 3).map((tag, index) => (
                          <Badge key={index} variant="neutral" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {client.tags.length > 3 && (
                          <Badge variant="neutral" className="text-xs">
                            +{client.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-xs text-gray-500">
                      {client.industry && (
                        <div className="flex items-center gap-1">
                          <BuildingOfficeIcon className="w-3 h-3" />
                          <span className="truncate">{client.industry}</span>
                        </div>
                      )}
                      {client.createdAt && (
                        <span>{dayjs(client.createdAt).format('MMM D, YYYY')}</span>
                      )}
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardBody>
                <div className="space-y-3">
                  {clients.map((client) => (
                    <div
                      key={client._id}
                      className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer"
                      onClick={() => navigate(`/clients/${client._id}`)}
                    >
                      {/* Avatar */}
                      <Avatar
                        src={client.photo ? (client.photo.startsWith('http') ? client.photo : process.env.REACT_APP_SOCKET_URL + client.photo) : null}
                        name={client.name}
                        size="md"
                      />

                      {/* Client Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-neutral-dark">
                            {client.name}
                          </h3>
                          <StatusBadge status={client.status} />
                          {client.score > 0 && (
                            <div className="flex items-center gap-1">
                              <StarRating value={client.score} max={5} readonly size="sm" />
                              <span className="text-sm text-gray-500">{client.score}/5</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-2">
                          {client.company && (
                            <div className="flex items-center gap-1">
                              <BuildingOfficeIcon className="w-4 h-4" />
                              <span>{client.company}</span>
                            </div>
                          )}
                          {client.email && (
                            <div className="flex items-center gap-1">
                              <EnvelopeIcon className="w-4 h-4" />
                              <span className="truncate max-w-xs">{client.email}</span>
                            </div>
                          )}
                          {client.phone && (
                            <div className="flex items-center gap-1">
                              <PhoneIcon className="w-4 h-4" />
                              <span>{client.phone}</span>
                            </div>
                          )}
                          {client.industry && (
                            <div className="flex items-center gap-1">
                              <TagIcon className="w-4 h-4" />
                              <span>{client.industry}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1 text-primary">
                            <FolderIcon className="w-4 h-4" />
                            <span className="font-medium">
                              {client.projectCount || client.projects?.length || 0} {t('projects.title')}
                            </span>
                          </div>
                          {client.totalRevenue > 0 && (
                            <div className="flex items-center gap-1 text-success">
                              <CurrencyDollarIcon className="w-4 h-4" />
                              <span className="font-medium">
                                {formatCurrency(client.totalRevenue, client.currency)}
                              </span>
                            </div>
                          )}
                          {client.createdAt && (
                            <div className="flex items-center gap-1 text-gray-400">
                              <span>{dayjs(client.createdAt).format('MMM D, YYYY')}</span>
                            </div>
                          )}
                        </div>
                        {client.tags && client.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {client.tags.slice(0, 5).map((tag, index) => (
                              <Badge key={index} variant="neutral" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}